import React from 'react';
import type { StepType } from '@reactour/tour';
import { components as TourComponents } from '@reactour/tour';
import { lywOnboardingTourStyles } from './lywOnboardingTourSteps';

/**
 * Default @reactour/tour styles (subset) — merged with {@link lywOnboardingTourStyles} the same way the library does.
 */
const defaultTourStyles = {
  controls: () => ({
    display: 'flex',
    marginTop: 24,
    alignItems: 'center',
    justifyContent: 'space-between',
  }),
  dot: ({
    current,
    disabled,
    past,
  }: {
    current?: boolean;
    disabled?: boolean;
    past?: boolean;
  }) => {
    const base = {
      counterIncrement: 'dot',
      width: 8,
      height: 8,
      borderRadius: '100%',
      padding: 0,
      display: 'block',
      margin: '4px 0',
      transition: 'opacity 0.3s, transform 0.3s',
      cursor: disabled ? 'not-allowed' : 'pointer',
    } as const;

    if (current) {
      return {
        ...base,
        border: '0',
        transform: 'scale(1.25)',
        color: 'var(--reactour-accent, #007aff)',
        background: 'var(--reactour-accent, #007aff)',
      };
    }
    if (past) {
      return {
        ...base,
        border: '0',
        transform: 'scale(1)',
        color: '#848a85',
        background: '#848a85',
      };
    }
    return {
      ...base,
      border: '1px solid #848a85',
      transform: 'scale(1)',
      color: '#848a85',
      background: 'none',
    };
  },
  button: ({ disabled }: { kind?: string; disabled?: boolean }) => ({
    display: 'block',
    padding: 0,
    border: 0,
    background: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
} as const;

type StylesProp = typeof lywOnboardingTourStyles & Record<string, (base: Record<string, unknown>, state?: unknown) => Record<string, unknown>>;

function stylesMatcher(styles: Partial<StylesProp>) {
  return (key: 'controls' | 'dot' | 'button' | 'arrow', state?: unknown) => {
    let base: Record<string, unknown>;
    if (key === 'controls') {
      base = defaultTourStyles.controls();
    } else if (key === 'dot') {
      base = defaultTourStyles.dot(
        state as { current?: boolean; disabled?: boolean; past?: boolean },
      );
    } else if (key === 'button') {
      base = defaultTourStyles.button(state as { kind?: string; disabled?: boolean });
    } else {
      base = {};
    }
    const custom = styles[key as keyof typeof styles];
    return typeof custom === 'function' ? (custom as (b: Record<string, unknown>, s: unknown) => Record<string, unknown>)(base, state) : base;
  };
}

type ArrowComponent = React.ComponentType<{
  styles?: Partial<StylesProp>;
  inverted?: boolean;
  disabled?: boolean;
}>;

export type LywTourTwoRowNavigationProps = {
  styles?: Partial<StylesProp>;
  steps: StepType[];
  setCurrentStep: (updater: number | ((prev: number) => number)) => void;
  currentStep: number;
  setIsOpen: (open: boolean) => void;
  nextButton?: (props: Record<string, unknown>) => React.ReactNode;
  prevButton?: (props: Record<string, unknown>) => React.ReactNode;
  disableDots?: boolean;
  hideDots?: boolean;
  hideButtons?: boolean;
  disableAll?: boolean;
  rtl?: boolean;
  Arrow?: ArrowComponent;
};

/**
 * Same behavior as @reactour/tour Navigation, but progress dots are always in two rows with
 * counts split as evenly as possible (first row ⌈n/2⌉, second row ⌊n/2⌋).
 */
export function LywTourTwoRowNavigation({
  styles = {},
  steps,
  setCurrentStep,
  currentStep,
  setIsOpen,
  nextButton,
  prevButton,
  disableDots,
  hideDots,
  hideButtons,
  disableAll,
  rtl,
  Arrow = TourComponents.Arrow,
}: LywTourTwoRowNavigationProps) {
  const stepsLength = steps.length;
  const getStyles = stylesMatcher(styles ?? {});
  const firstRowCount = Math.ceil(stepsLength / 2);
  const firstIndices = Array.from({ length: firstRowCount }, (_, i) => i);
  const secondIndices = Array.from({ length: stepsLength - firstRowCount }, (_, i) => i + firstRowCount);

  const Button = ({
    onClick,
    kind = 'next',
    children,
    hideArrow,
  }: {
    onClick?: () => void;
    kind?: 'next' | 'prev';
    children?: React.ReactNode;
    hideArrow?: boolean;
  }) => {
    function clickHandler() {
      if (!disableAll) {
        if (onClick && typeof onClick === 'function') {
          onClick();
        } else if (kind === 'next') {
          setCurrentStep(Math.min(currentStep + 1, stepsLength - 1));
        } else {
          setCurrentStep(Math.max(currentStep - 1, 0));
        }
      }
    }

    const disabledNext = stepsLength - 1 === currentStep;
    const disabledPrev = currentStep === 0;

    return (
      <button
        type="button"
        style={
          getStyles('button', {
            kind,
            disabled: disableAll ? disableAll : kind === 'next' ? disabledNext : disabledPrev,
          }) as React.CSSProperties
        }
        onClick={clickHandler}
        aria-label={`Go to ${kind} step`}
      >
        {!hideArrow ? (
          <Arrow
            styles={(styles ?? lywOnboardingTourStyles) as Partial<StylesProp>}
            inverted={rtl ? kind === 'prev' : kind === 'next'}
            disabled={disableAll ? !!disableAll : kind === 'next' ? disabledNext : disabledPrev}
          />
        ) : null}
        {children}
      </button>
    );
  };

  const renderDot = (index: number) => {
    const step = steps[index];
    const aria =
      (step && typeof step === 'object' && 'navDotAriaLabel' in step && (step as { navDotAriaLabel?: string }).navDotAriaLabel) ||
      `Go to step ${index + 1}`;

    return (
      <button
        type="button"
        key={`navigation_dot_${index}`}
        style={
          getStyles('dot', {
            current: index === currentStep,
            past: index < currentStep,
            disabled: disableDots || disableAll,
          }) as React.CSSProperties
        }
        onClick={() => {
          if (!disableDots && !disableAll) setCurrentStep(index);
        }}
        aria-label={aria}
      />
    );
  };

  const rowStyle = (): React.CSSProperties => ({
    display: 'flex',
    flexWrap: 'nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    counterReset: 'dot',
  });

  return (
    <div style={getStyles('controls', {}) as React.CSSProperties} dir={rtl ? 'rtl' : 'ltr'}>
      {!hideButtons ? (
        prevButton && typeof prevButton === 'function' ? (
          (prevButton as (p: Record<string, unknown>) => React.ReactNode)({
            Button,
            setCurrentStep,
            currentStep,
            stepsLength,
            setIsOpen,
            steps,
          })
        ) : (
          <Button kind="prev" />
        )
      ) : null}

      {!hideDots ? (
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            marginLeft: 8,
            marginRight: 8,
          }}
        >
          <div style={rowStyle()}>{firstIndices.map((i) => renderDot(i))}</div>
          {secondIndices.length > 0 ? <div style={rowStyle()}>{secondIndices.map((i) => renderDot(i))}</div> : null}
        </div>
      ) : null}

      {!hideButtons ? (
        nextButton && typeof nextButton === 'function' ? (
          (nextButton as (p: Record<string, unknown>) => React.ReactNode)({
            Button,
            setCurrentStep,
            currentStep,
            stepsLength,
            setIsOpen,
            steps,
          })
        ) : (
          <Button kind="next" />
        )
      ) : null}
    </div>
  );
}
